import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import HubTabs from './HubTabs';
import { StatusBadge } from './DataGrid';
import { IconPencil, IconKey, IconCheck, IconTrash } from './Icons';
import { merchantCityLabel, merchantRegionLabel, merchantCountryLabel } from '../utils/merchantAddressForm';

function today() { return new Date().toISOString().slice(0, 10); }
function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

function fmtMoney(n, sym = 'ر.س') {
  const v = Number(n) || 0;
  return `${v.toLocaleString('ar-SA', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${sym}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return String(iso).slice(0, 10); }
}

const DATE_PRESETS = [
  { key: '7', label: '7 أيام', days: 7 },
  { key: '30', label: '30 يوم', days: 30 },
  { key: '90', label: '3 أشهر', days: 90 },
];

const TABS = [
  { id: 'overview', label: 'نظرة عامة', icon: '📊' },
  { id: 'details', label: 'البيانات', icon: '📋' },
  { id: 'financial', label: 'المالية', icon: '💰' },
  { id: 'reports', label: 'التقارير', icon: '📈' },
  { id: 'actions', label: 'الإجراءات', icon: '⚙️' },
];

function SummaryCard({ label, value, tone }) {
  return (
    <div className={`vi-detail-card${tone ? ` vi-detail-card--${tone}` : ''}`}>
      <span className="vi-detail-card__value">{value}</span>
      <span className="vi-detail-card__label">{label}</span>
    </div>
  );
}

function DetailRow({ label, value, ltr }) {
  return (
    <div className="vi-detail-row">
      <span className="vi-detail-row__label">{label}</span>
      <span className={`vi-detail-row__value${ltr ? ' vi-detail-row__value--ltr' : ''}`}>{value || '—'}</span>
    </div>
  );
}

function DateRangeBar({ preset, onPreset, from, to, onFrom, onTo }) {
  return (
    <div className="vi-detail-dates">
      <div className="vi-detail-dates__presets">
        {DATE_PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            className={preset === p.key ? 'vi-detail-preset vi-detail-preset--active' : 'vi-detail-preset'}
            onClick={() => onPreset(p.key, p.days)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="vi-detail-dates__inputs">
        <input type="date" className="vi-input vi-input--compact" value={from} onChange={(e) => onFrom(e.target.value)} dir="ltr" />
        <span className="vi-detail-dates__sep">→</span>
        <input type="date" className="vi-input vi-input--compact" value={to} onChange={(e) => onTo(e.target.value)} dir="ltr" />
      </div>
    </div>
  );
}

export default function MerchantDetailHub({
  merchant: initialMerchant,
  onClose,
  canManage = false,
  branchesActive = false,
  onEdit,
  onPassword,
  onDelete,
  onReactivate,
  embedded = false,
}) {
  const [tab, setTab] = useState('overview');
  const [merchant, setMerchant] = useState(initialMerchant);
  const [loadingDetail, setLoadingDetail] = useState(true);

  const [datePreset, setDatePreset] = useState('30');
  const [dateFrom, setDateFrom] = useState(daysAgo(30));
  const [dateTo, setDateTo] = useState(today());

  const [payoutSummary, setPayoutSummary] = useState(null);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [pendingOrders, setPendingOrders] = useState(null);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const merchantId = merchant?.id;

  const applyPreset = useCallback((key, days) => {
    setDatePreset(key);
    if (days) {
      setDateFrom(daysAgo(days));
      setDateTo(today());
    }
  }, []);

  useEffect(() => {
    if (!merchantId) return;
    setLoadingDetail(true);
    api.getMerchant(merchantId)
      .then((data) => { if (data?.id) setMerchant((m) => ({ ...m, ...data })); })
      .catch(() => {})
      .finally(() => setLoadingDetail(false));
  }, [merchantId]);

  const loadPayout = useCallback(async () => {
    if (!merchantId) return;
    setPayoutLoading(true);
    try {
      const data = await api.getMerchantPayoutSummary({ date_from: dateFrom, date_to: dateTo });
      setPayoutSummary(data);
    } catch { setPayoutSummary(null); }
    finally { setPayoutLoading(false); }
  }, [merchantId, dateFrom, dateTo]);

  const loadReport = useCallback(async () => {
    if (!merchantId) return;
    setReportLoading(true);
    try {
      const data = await api.getReportsMerchantPerformance({
        date_from: dateFrom,
        date_to: dateTo,
        merchant_id: merchantId,
      });
      setReportData(data);
    } catch { setReportData(null); }
    finally { setReportLoading(false); }
  }, [merchantId, dateFrom, dateTo]);

  const loadPendingOrders = useCallback(async () => {
    if (!merchantId) return;
    setOrdersLoading(true);
    try {
      const data = await api.getMerchantPayoutOrders({
        merchant_id: merchantId,
        date_from: dateFrom,
        date_to: dateTo,
        financial_status: 'pending',
      });
      setPendingOrders(data);
    } catch { setPendingOrders(null); }
    finally { setOrdersLoading(false); }
  }, [merchantId, dateFrom, dateTo]);

  useEffect(() => {
    if (tab === 'overview' || tab === 'financial') loadPayout();
    if (tab === 'overview' || tab === 'reports') loadReport();
    if (tab === 'overview' || tab === 'financial') loadPendingOrders();
  }, [tab, loadPayout, loadReport, loadPendingOrders]);

  const finMerchant = useMemo(
    () => (payoutSummary?.merchants || []).find((m) => String(m.id) === String(merchantId)),
    [payoutSummary, merchantId],
  );

  const reportRow = useMemo(
    () => (reportData?.rows || []).find((r) => String(r.id) === String(merchantId)) || reportData?.rows?.[0],
    [reportData, merchantId],
  );

  const sym = payoutSummary?.currency_symbol || reportData?.currency_symbol || 'ر.س';
  const summary = reportData?.summary || {};
  const statusDist = reportData?.status_distribution || [];

  const payoutsLink = `/merchant-payouts?merchant=${merchantId}&from=${dateFrom}&to=${dateTo}&preset=${datePreset}`;
  const reportsLink = `/reports?tab=merchants&merchant=${merchantId}&from=${dateFrom}&to=${dateTo}`;

  return (
    <div className={`vi-detail-hub${embedded ? ' vi-detail-hub--embedded' : ''}`}>
      <header className="vi-detail-hub__header">
        <div className="vi-detail-hub__header-main">
          <span className="vi-detail-hub__avatar" aria-hidden>🏪</span>
          <div className="vi-detail-hub__title-block">
            <h2 className="vi-detail-hub__title">{merchant?.name || '—'}</h2>
            <div className="vi-detail-hub__meta">
              <StatusBadge active={!!merchant?.is_active} />
              {merchant?.username && (
                <span className="vi-detail-hub__username" dir="ltr">{merchant.username}</span>
              )}
              {branchesActive && merchant?.branch_name && (
                <span className="vi-detail-hub__branch">🏢 {merchant.branch_name}</span>
              )}
            </div>
          </div>
        </div>
        {onClose && (
          <button type="button" className="vi-detail-hub__close" onClick={onClose} aria-label="إغلاق">
            ✕
          </button>
        )}
      </header>

      <HubTabs tabs={TABS} active={tab} onChange={setTab} />

      <div className="vi-tab-panel">
        {tab === 'overview' && (
          <div className="vi-detail-panel">
            {loadingDetail || payoutLoading || reportLoading ? (
              <p className="vi-detail-loading">جاري التحميل...</p>
            ) : (
              <>
                <div className="vi-detail-cards">
                  <SummaryCard label="إجمالي الطلبات" value={summary.total_orders ?? reportRow?.total_orders ?? 0} />
                  <SummaryCard label="تم التسليم" value={summary.delivered ?? reportRow?.delivered ?? 0} tone="success" />
                  <SummaryCard label="نسبة التسليم" value={`${summary.delivery_rate ?? reportRow?.delivery_rate ?? '0'}%`} tone="gold" />
                  <SummaryCard
                    label="مستحق الدفع"
                    value={fmtMoney(finMerchant?.pending_amount, sym)}
                    tone={finMerchant?.pending_amount > 0 ? 'warning' : undefined}
                  />
                </div>
                <div className="vi-detail-section">
                  <h3 className="vi-detail-section__title">معلومات سريعة</h3>
                  <div className="vi-detail-rows">
                    <DetailRow label="جهة الاتصال" value={merchant?.contact_person} />
                    <DetailRow label="الهاتف" value={merchant?.phone} ltr />
                    <DetailRow label="البريد" value={merchant?.email} ltr />
                    <DetailRow label="المدينة" value={merchantCityLabel(merchant)} />
                    {branchesActive && <DetailRow label="الفرع" value={merchant?.branch_name} />}
                    <DetailRow label="تاريخ التسجيل" value={fmtDate(merchant?.created_at)} />
                  </div>
                </div>
                <div className="vi-detail-quick-links">
                  <Link to={payoutsLink} className="vi-detail-link-btn">💰 المدفوعات والتسويات</Link>
                  <button type="button" className="vi-detail-link-btn vi-detail-link-btn--ghost" onClick={() => setTab('reports')}>
                    📈 تقرير الأداء
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'details' && (
          <div className="vi-detail-panel">
            {loadingDetail ? (
              <p className="vi-detail-loading">جاري التحميل...</p>
            ) : (
              <>
                <div className="vi-detail-section">
                  <h3 className="vi-detail-section__title">البيانات الأساسية</h3>
                  <div className="vi-detail-rows">
                    <DetailRow label="اسم التاجر" value={merchant?.name} />
                    <DetailRow label="جهة الاتصال" value={merchant?.contact_person} />
                    <DetailRow label="الهاتف" value={merchant?.phone} ltr />
                    <DetailRow label="البريد الإلكتروني" value={merchant?.email} ltr />
                    <DetailRow label="اسم المستخدم" value={merchant?.username} ltr />
                    <DetailRow label="الحالة" value={merchant?.is_active ? 'نشط' : 'غير نشط'} />
                  </div>
                </div>
                <div className="vi-detail-section">
                  <h3 className="vi-detail-section__title">العنوان والموقع</h3>
                  <div className="vi-detail-rows">
                    <DetailRow label="الدولة" value={merchantCountryLabel(merchant)} />
                    <DetailRow label="المدينة" value={merchantCityLabel(merchant)} />
                    <DetailRow label="المنطقة" value={merchantRegionLabel(merchant)} />
                    <DetailRow label="العنوان" value={merchant?.address} />
                    <DetailRow label="ملاحظات العنوان" value={merchant?.address_description} />
                    {(merchant?.address_lat || merchant?.address_lng) && (
                      <DetailRow
                        label="الإحداثيات"
                        value={`${merchant?.address_lat || '—'}, ${merchant?.address_lng || '—'}`}
                        ltr
                      />
                    )}
                    {branchesActive && <DetailRow label="الفرع" value={merchant?.branch_name} />}
                  </div>
                </div>
                {canManage && (
                  <button type="button" className="vi-btn vi-btn-outline" onClick={() => onEdit?.(merchant)} style={{ alignSelf: 'flex-start' }}>
                    <IconPencil /> تعديل البيانات
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {tab === 'financial' && (
          <div className="vi-detail-panel">
            <DateRangeBar
              preset={datePreset}
              onPreset={applyPreset}
              from={dateFrom}
              to={dateTo}
              onFrom={(v) => { setDatePreset('custom'); setDateFrom(v); }}
              onTo={(v) => { setDatePreset('custom'); setDateTo(v); }}
            />
            {payoutLoading ? (
              <p className="vi-detail-loading">جاري التحميل...</p>
            ) : (
              <>
                <div className="vi-detail-cards">
                  <SummaryCard label="مستحق الدفع" value={fmtMoney(finMerchant?.pending_amount, sym)} tone="warning" />
                  <SummaryCard label="تم الدفع" value={fmtMoney(finMerchant?.paid_amount, sym)} tone="success" />
                  <SummaryCard label="طلبات بانتظار الدفع" value={finMerchant?.pending_payout ?? 0} />
                  <SummaryCard label="رسوم الشحن" value={fmtMoney(finMerchant?.total_shipping_cost, sym)} />
                </div>
                <div className="vi-detail-section">
                  <h3 className="vi-detail-section__title">طلبات بانتظار التسوية</h3>
                  {ordersLoading ? (
                    <p className="vi-detail-muted">جاري التحميل...</p>
                  ) : (
                    <p className="vi-detail-stat-line">
                      <strong>{pendingOrders?.rows?.length ?? pendingOrders?.total ?? 0}</strong>
                      {' '}طلب في الفترة المحددة
                    </p>
                  )}
                </div>
                <Link to={payoutsLink} className="vi-btn vi-btn-primary vi-detail-full-btn">
                  فتح صفحة المدفوعات والتسويات
                </Link>
              </>
            )}
          </div>
        )}

        {tab === 'reports' && (
          <div className="vi-detail-panel">
            <DateRangeBar
              preset={datePreset}
              onPreset={applyPreset}
              from={dateFrom}
              to={dateTo}
              onFrom={(v) => { setDatePreset('custom'); setDateFrom(v); }}
              onTo={(v) => { setDatePreset('custom'); setDateTo(v); }}
            />
            {reportLoading ? (
              <p className="vi-detail-loading">جاري التحميل...</p>
            ) : (
              <>
                <div className="vi-detail-cards">
                  <SummaryCard label="إجمالي الطلبات" value={summary.total_orders ?? 0} />
                  <SummaryCard label="تم التسليم" value={summary.delivered ?? 0} tone="success" />
                  <SummaryCard label="مرتجعة" value={summary.returned ?? 0} />
                  <SummaryCard label="ملغاة" value={summary.cancelled ?? 0} />
                  <SummaryCard label="قيد التنفيذ" value={summary.in_progress ?? 0} />
                  <SummaryCard label="فاشلة" value={summary.failed ?? 0} tone="danger" />
                  <SummaryCard label="نسبة التسليم" value={`${summary.delivery_rate ?? '0'}%`} tone="gold" />
                  <SummaryCard label="إجمالي قيمة الطلبات" value={fmtMoney(summary.total_price, sym)} />
                </div>
                {statusDist.length > 0 && (
                  <div className="vi-detail-section">
                    <h3 className="vi-detail-section__title">توزيع الحالات</h3>
                    <div className="vi-detail-status-chips">
                      {statusDist.map((s) => (
                        <span key={s.status} className="vi-detail-status-chip">
                          <strong>{s.count}</strong> {s.status}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <Link to={reportsLink} className="vi-btn vi-btn-outline vi-detail-full-btn">
                  عرض التقارير الكاملة
                </Link>
              </>
            )}
          </div>
        )}

        {tab === 'actions' && (
          <div className="vi-detail-panel">
            <div className="vi-detail-actions-grid">
              {canManage && merchant?.is_active && (
                <>
                  <button type="button" className="vi-detail-action-card" onClick={() => onEdit?.(merchant)}>
                    <span className="vi-detail-action-card__icon"><IconPencil /></span>
                    <span className="vi-detail-action-card__label">تعديل البيانات</span>
                    <span className="vi-detail-action-card__hint">الاسم، العنوان، الفرع</span>
                  </button>
                  {merchant?.username && (
                    <button type="button" className="vi-detail-action-card" onClick={() => onPassword?.(merchant)}>
                      <span className="vi-detail-action-card__icon"><IconKey /></span>
                      <span className="vi-detail-action-card__label">تغيير كلمة المرور</span>
                      <span className="vi-detail-action-card__hint">بوابة التاجر</span>
                    </button>
                  )}
                  <button type="button" className="vi-detail-action-card vi-detail-action-card--danger" onClick={() => onDelete?.(merchant.id)}>
                    <span className="vi-detail-action-card__icon"><IconTrash /></span>
                    <span className="vi-detail-action-card__label">حذف التاجر</span>
                    <span className="vi-detail-action-card__hint">إلغاء التفعيل</span>
                  </button>
                </>
              )}
              {canManage && !merchant?.is_active && (
                <button type="button" className="vi-detail-action-card vi-detail-action-card--success" onClick={() => onReactivate?.(merchant.id)}>
                  <span className="vi-detail-action-card__icon"><IconCheck /></span>
                  <span className="vi-detail-action-card__label">إعادة التفعيل</span>
                  <span className="vi-detail-action-card__hint">تنشيط الحساب</span>
                </button>
              )}
              <Link to={payoutsLink} className="vi-detail-action-card vi-detail-action-card--link">
                <span className="vi-detail-action-card__icon" aria-hidden>💰</span>
                <span className="vi-detail-action-card__label">المدفوعات</span>
                <span className="vi-detail-action-card__hint">تسويات ومستحقات</span>
              </Link>
              <Link to={reportsLink} className="vi-detail-action-card vi-detail-action-card--link">
                <span className="vi-detail-action-card__icon" aria-hidden>📈</span>
                <span className="vi-detail-action-card__label">التقارير</span>
                <span className="vi-detail-action-card__hint">أداء الطلبات</span>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}